import { useEffect, useMemo, useRef, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import VariantsSheet from "@/components/admin/VariantsSheet";
import ProductPhotosDialog from "@/components/admin/ProductPhotosDialog";
import api from "@/lib/api";
import { Plus, Pencil, Trash2, Search, Layers, Images, Copy, Upload, X, Loader2, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { name: "", name_en: "", name_de: "", brand: "Fiat", model: "", years: "", price: 0, compare_at_price: 0, category: "9-inch", screen_size: "9\"", ram: "4GB", rom: "64GB", badge: "", rating: 0, reviews_count: 0, stock: 0, description: "", description_en: "", description_de: "", specs: {}, images: [], is_accessory: false, related_product_ids: [] };

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [variantsFor, setVariantsFor] = useState(null);
  const [photosFor, setPhotosFor] = useState(null);
  const [reimporting, setReimporting] = useState(false);
  const [accessoriesList, setAccessoriesList] = useState([]);
  const [relatedQuery, setRelatedQuery] = useState("");
  const [uploadingInline, setUploadingInline] = useState(false);
  const [existingCategories, setExistingCategories] = useState([]);
  const fileInputRef = useRef(null);

  const load = () => api.get("/products", { params: { search: q || undefined, include_gifts: true } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [q]);

  // Load all accessory products once, used by the related-products picker in the edit dialog.
  useEffect(() => {
    api.get("/products", { params: { is_accessory: true } }).then((r) => setAccessoriesList(r.data || [])).catch(() => setAccessoriesList([]));
    api.get("/catalog/categories").then((r) => setExistingCategories(r.data || [])).catch(() => setExistingCategories([]));
  }, []);

  const sorted = useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => {
      const va = a[sortKey] ?? ""; const vb = b[sortKey] ?? "";
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [items, sortKey, sortDir]);

  const openNew = () => { setEditing({ __new: true }); setForm(EMPTY); };
  const openEdit = (p) => {
    // Guard: never let openEdit run with a product missing its id (would silently POST
    // a duplicate on save). Fetch fresh from the API just to be sure.
    if (!p || !p.id) { toast.error("Prodotto senza ID, ricarica la pagina"); return; }
    setEditing(p);
    // Preserve the description EXACTLY as stored (spaces, tabs, newlines). If empty,
    // extract a plain-text snapshot from the legacy body_html so the admin sees what's
    // currently rendered on the storefront and can edit it.
    const htmlToPlain = (html) => (html || "")
      .replace(/\r\n?/g, "\n")
      .replace(/<\s*br\s*\/?>/gi, "\n")
      .replace(/<\/\s*p\s*>\s*<\s*p[^>]*>/gi, "\n\n")
      .replace(/<\s*p[^>]*>/gi, "").replace(/<\/\s*p\s*>/gi, "")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
      .replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n")
      .trim();
    const description = (p.description && p.description.length > 0) ? p.description : htmlToPlain(p.body_html);
    setForm({ ...EMPTY, ...p, description, compare_at_price: p.compare_at_price || 0, specs: p.specs || {}, images: p.images || [] });
  };

  const save = async () => {
    const payload = { ...form, price: Number(form.price), compare_at_price: form.compare_at_price ? Number(form.compare_at_price) : null, stock: Number(form.stock), rating: Number(form.rating), reviews_count: Number(form.reviews_count), images: typeof form.images === "string" ? form.images.split("\n").filter(Boolean) : form.images };
    // Explicit new-vs-edit switch instead of relying on falsy id: prevents accidental POSTs.
    const isNew = editing?.__new === true || !editing?.id;
    try {
      if (isNew) {
        await api.post("/admin/products", payload);
        toast.success("Prodotto creato");
      } else {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Prodotto aggiornato");
      }
      setEditing(null);
      load();
      api.get("/products", { params: { is_accessory: true } }).then((r) => setAccessoriesList(r.data || [])).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Elimina ${p.name}?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Eliminato");
    load();
  };

  // Upload one or more image files from device (camera / gallery) directly into
  // the product edit dialog. Requires an existing product id — creates the
  // record first if the user is editing a brand-new item.
  const handleInlineUpload = async (fileList) => {
    if (!fileList || !fileList.length) return;
    let productId = editing?.id;
    // If we're on a NEW product, save it first so we have an id.
    if (!productId || editing?.__new) {
      try {
        const payload = { ...form, price: Number(form.price) || 0, compare_at_price: form.compare_at_price ? Number(form.compare_at_price) : null, stock: Number(form.stock) || 0, rating: Number(form.rating) || 0, reviews_count: Number(form.reviews_count) || 0, images: [] };
        if (!payload.name?.trim()) { toast.error("Inserisci prima il nome del prodotto"); return; }
        const r = await api.post("/admin/products", payload);
        productId = r.data.id;
        setEditing(r.data);
        toast.success("Prodotto creato, ora carico le foto…");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Errore creazione prodotto");
        return;
      }
    }
    setUploadingInline(true);
    let added = 0;
    try {
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", file);
        try {
          const r = await api.post(`/admin/products/${productId}/photos`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setForm((f) => ({ ...f, images: [r.data.url, ...(f.images || [])] }));
          added++;
        } catch (e) {
          toast.error(`${file.name}: ${e.response?.data?.detail || "errore upload"}`);
        }
      }
      if (added > 0) { toast.success(`${added} foto caricate`); load(); }
    } finally {
      setUploadingInline(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeInlineImage = async (url) => {
    if (!editing?.id) {
      setForm((f) => ({ ...f, images: (f.images || []).filter((u) => u !== url) }));
      return;
    }
    if (!window.confirm("Rimuovere questa foto?")) return;
    try {
      await api.delete(`/admin/products/${editing.id}/photos`, { params: { url } });
      setForm((f) => ({ ...f, images: (f.images || []).filter((u) => u !== url) }));
      toast.success("Foto rimossa");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore rimozione");
    }
  };


  const duplicate = async (p) => {
    if (!window.confirm(`Duplicare "${p.name.slice(0, 60)}" con tutte le sue varianti?`)) return;
    try {
      const r = await api.post(`/admin/products/${p.id}/duplicate`);
      toast.success(`Prodotto duplicato: ${r.data.name.slice(0, 60)}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore duplicazione");
    }
  };

  const triggerReimport = async () => {
    if (!window.confirm("Avviare la sincronizzazione da xdrivesound? L'operazione richiede 2-3 minuti in background.")) return;
    setReimporting(true);
    try {
      await api.post("/admin/products/reimport");
      toast.success("Sincronizzazione avviata. Torna tra qualche minuto per vedere i risultati.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore avvio sync");
    } finally {
      setTimeout(() => setReimporting(false), 3000);
    }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/products/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8;" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `audrivio-products-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV scaricato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore export");
    }
  };

  const importCsv = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    if (!window.confirm(`Importare ${file.name}? I prodotti esistenti (stesso handle) verranno aggiornati; quelli nuovi verranno creati.`)) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/products/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import OK · Creati: ${r.data.created} · Aggiornati: ${r.data.updated} · Saltati: ${r.data.skipped_missing_name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore import");
    }
  };

  const th = (key, label) => (
    <th onClick={() => { if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc"); else { setSortKey(key); setSortDir("asc"); } }} className="px-4 py-3 text-left font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted cursor-pointer select-none">
      {label} {sortKey === key ? (sortDir === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  const specsText = Object.entries(form.specs || {}).map(([k, v]) => `${k}: ${v}`).join("\n");
  const parseSpecs = (txt) => {
    const obj = {};
    txt.split("\n").forEach((l) => {
      const [k, ...rest] = l.split(":");
      if (k && rest.length) obj[k.trim()] = rest.join(":").trim();
    });
    return obj;
  };

  return (
    <AdminLayout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">Catalogo</div>
          <h1 className="font-admin text-2xl font-bold">Prodotti</h1>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-xd-muted" />
            <Input placeholder="Cerca prodotti…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="products-search" className="pl-9 w-full sm:w-64" />
          </div>
          <button type="button" onClick={exportCsv} data-testid="products-export-csv" title="Esporta tutti i prodotti come CSV (incluso varianti)" className="rounded-lg px-3 h-10 text-sm inline-flex items-center gap-2 whitespace-nowrap bg-white border border-xd-border hover:border-xd-cyan hover:text-xd-cyanHover transition-colors">
            <Download className="w-4 h-4" /> <span className="hidden sm:inline">Esporta CSV</span>
          </button>
          <label
            data-testid="products-import-csv-label"
            title="Importa/aggiorna prodotti da CSV (raggruppati per handle)"
            className="rounded-lg px-3 h-10 text-sm inline-flex items-center gap-2 whitespace-nowrap bg-white border border-xd-border hover:border-xd-cyan hover:text-xd-cyanHover transition-colors cursor-pointer"
          >
            <Upload className="w-4 h-4" /> <span className="hidden sm:inline">Importa CSV</span>
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => { importCsv(e.target.files); e.target.value = ""; }}
              data-testid="products-import-csv-input"
            />
          </label>
          <button type="button" onClick={openNew} data-testid="products-new-btn" className="btn-cta rounded-lg px-4 h-10 text-sm inline-flex items-center gap-2 whitespace-nowrap"><Plus className="w-4 h-4" /> Nuovo</button>
        </div>
      </div>

      <div className="bg-white border border-xd-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-xd-light border-b border-xd-border">
            <tr>
              {th("name", "Nome")}{th("brand", "Marca")}{th("price", "Prezzo")}{th("stock", "Stock")}{th("badge", "Badge")}
              <th className="px-4 py-3 text-right font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <tr key={p.id} className="border-b border-xd-border last:border-b-0 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{p.name}</td>
                <td className="px-4 py-3 text-xd-muted">{p.brand} · {p.model}</td>
                <td className="px-4 py-3 font-mono-tech">€ {p.price.toFixed(2)}</td>
                <td className="px-4 py-3">{p.stock}</td>
                <td className="px-4 py-3 text-xs">{p.badge || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1.5 items-center">
                    <button
                      type="button"
                      onClick={() => setVariantsFor(p)}
                      data-testid={`variants-${p.id}`}
                      title={(p.variants?.length || 0) > 0 ? `Gestisci varianti (${p.variants.length})` : "Aggiungi la prima variante"}
                      className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-xd-orange/10 hover:bg-xd-orange/20 text-xd-orange border border-xd-orange/20 transition-colors relative"
                    >
                      <Layers className="w-5 h-5" />
                      {(p.variants?.length || 0) > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 text-[10px] font-mono-tech bg-xd-orange text-white rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center font-semibold">
                          {p.variants.length}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPhotosFor(p)}
                      data-testid={`photos-${p.id}`}
                      title={`Gestisci foto (${(p.images || []).length})`}
                      className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-xd-cyan/10 hover:bg-xd-cyan/20 text-xd-cyanHover border border-xd-cyan/20 transition-colors relative"
                    >
                      <Images className="w-5 h-5" />
                      {(p.images?.length || 0) > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 text-[10px] font-mono-tech bg-xd-cyan text-white rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center font-semibold">
                          {p.images.length}
                        </span>
                      )}
                    </button>
                    <button type="button" onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} title="Modifica prodotto" className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 transition-colors"><Pencil className="w-5 h-5" /></button>
                    <button type="button" onClick={() => duplicate(p)} data-testid={`duplicate-${p.id}`} title="Duplica prodotto" className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 border border-emerald-200 transition-colors"><Copy className="w-5 h-5" /></button>
                    <button type="button" onClick={() => del(p)} data-testid={`del-${p.id}`} title="Elimina prodotto" className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 border border-red-200 transition-colors"><Trash2 className="w-5 h-5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <VariantsSheet
        product={variantsFor}
        open={!!variantsFor}
        onOpenChange={(o) => !o && setVariantsFor(null)}
        onSaved={load}
      />

      <ProductPhotosDialog
        product={photosFor}
        open={!!photosFor}
        onOpenChange={(o) => !o && setPhotosFor(null)}
        onChanged={load}
      />

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-admin">{editing?.id ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="col-span-2" />
            <Input placeholder="Name (English) — opzionale, mostrato ai visitatori EN" value={form.name_en || ""} onChange={(e) => setForm({ ...form, name_en: e.target.value })} data-testid="product-name-en" className="col-span-2" />
            <Input placeholder="Name (Deutsch) — opzionale, mostrato ai visitatori DE" value={form.name_de || ""} onChange={(e) => setForm({ ...form, name_de: e.target.value })} data-testid="product-name-de" className="col-span-2" />
            <Input placeholder="Marca" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
            <Input placeholder="Modello" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
            <Input placeholder="Anni (es. 2010-2020)" value={form.years} onChange={(e) => setForm({ ...form, years: e.target.value })} />
            <Input placeholder="Prezzo" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            <Input placeholder="Prezzo barrato" type="number" step="0.01" value={form.compare_at_price || ""} onChange={(e) => setForm({ ...form, compare_at_price: e.target.value })} />
            <Input placeholder="Schermo (7&quot; / 9&quot;)" value={form.screen_size} onChange={(e) => setForm({ ...form, screen_size: e.target.value })} />
            <div className="relative">
              <Input
                placeholder="Categoria (es. Cornici, Cavi, Accessori…)"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                list="admin-product-categories"
                data-testid="admin-product-category"
              />
              <datalist id="admin-product-categories">
                {existingCategories.map((c) => (<option key={c} value={c} />))}
              </datalist>
              {existingCategories.length > 0 && (
                <div className="mt-1 flex items-center gap-1 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest text-xd-muted font-mono-tech">Già usate:</span>
                  {existingCategories.slice(0, 8).map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, category: c }))}
                      data-testid={`admin-cat-chip-${c}`}
                      className={`text-[10px] rounded-full border px-2 py-0.5 transition-colors ${form.category === c ? "border-xd-cyan bg-xd-cyan/10 text-xd-cyanHover" : "border-xd-border text-xd-muted hover:border-xd-cyan/40 hover:text-xd-text"}`}
                    >
                      {c}
                    </button>
                  ))}
                  {existingCategories.length > 8 && (
                    <span className="text-[10px] text-xd-muted">+{existingCategories.length - 8}</span>
                  )}
                </div>
              )}
              <div className="mt-1 text-[10px] text-xd-muted leading-tight">
                Scrivi una nuova categoria oppure clicca una tra quelle già usate. Verrà creata automaticamente al salvataggio.
              </div>
            </div>
            <Input placeholder="RAM" value={form.ram} onChange={(e) => setForm({ ...form, ram: e.target.value })} />
            <Input placeholder="ROM" value={form.rom} onChange={(e) => setForm({ ...form, rom: e.target.value })} />
            <Input placeholder="Stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
            <select value={form.badge || ""} onChange={(e) => setForm({ ...form, badge: e.target.value })} className="border border-xd-border rounded-md px-3 py-2 text-sm">
              <option value="">Nessun badge</option>
              <option value="sale">In offerta</option>
              <option value="new">Nuovo</option>
              <option value="best_seller">Best seller</option>
            </select>
            <div className="col-span-2 space-y-1">
              <textarea placeholder="Descrizione (una riga vuota per un nuovo paragrafo — i ritorni a capo vengono mantenuti)" rows={8} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-description-textarea" className="w-full border border-xd-border rounded-md px-3 py-2 text-sm font-mono-tech leading-relaxed whitespace-pre-wrap" />
              <div className="text-[10px] text-xd-muted">Suggerimento: lascia una riga vuota per separare i paragrafi. La formattazione (a capo, spazi) viene rispettata sulla pagina prodotto pubblica.</div>
            </div>
            <div className="col-span-2 space-y-1">
              <textarea placeholder="Description (English) — opzionale, mostrata ai visitatori EN" rows={6} value={form.description_en || ""} onChange={(e) => setForm({ ...form, description_en: e.target.value })} data-testid="product-description-en-textarea" className="w-full border border-xd-border rounded-md px-3 py-2 text-sm font-mono-tech leading-relaxed whitespace-pre-wrap" />
              <div className="text-[10px] text-xd-muted">Se lasciata vuota, i visitatori EN vedranno la descrizione italiana. Ideale per i top 30 bestseller.</div>
            </div>
            <div className="col-span-2 space-y-1">
              <textarea placeholder="Beschreibung (Deutsch) — opzionale, mostrata ai visitatori DE" rows={6} value={form.description_de || ""} onChange={(e) => setForm({ ...form, description_de: e.target.value })} data-testid="product-description-de-textarea" className="w-full border border-xd-border rounded-md px-3 py-2 text-sm font-mono-tech leading-relaxed whitespace-pre-wrap" />
              <div className="text-[10px] text-xd-muted">Se lasciata vuota, i visitatori DE ricadono sull'inglese (o italiano se nemmeno EN è compilato).</div>
            </div>

            {/* Accessory toggle */}
            <label className="col-span-2 flex items-center gap-3 rounded-lg border border-dashed border-emerald-300 bg-emerald-50/50 px-4 py-3 cursor-pointer" data-testid="product-is-accessory-toggle">
              <input
                type="checkbox"
                checked={!!form.is_accessory}
                onChange={(e) => setForm({ ...form, is_accessory: e.target.checked })}
                className="w-5 h-5 accent-emerald-600"
              />
              <div>
                <div className="text-sm font-medium text-emerald-800">È un accessorio</div>
                <div className="text-xs text-emerald-700/80">Verrà mostrato nella categoria &quot;Accessori&quot; del catalogo e potrà essere selezionato come prodotto correlato.</div>
              </div>
            </label>

            {/* Related products picker */}
            {!form.is_accessory && (
              <div className="col-span-2 rounded-lg border border-xd-border bg-white p-3 space-y-3" data-testid="related-products-picker">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium">Prodotti correlati (accessori suggeriti)</div>
                    <div className="text-[11px] text-xd-muted">Selezionati: {form.related_product_ids?.length || 0} · Vengono mostrati sulla scheda prodotto.</div>
                  </div>
                  <Input
                    value={relatedQuery}
                    onChange={(e) => setRelatedQuery(e.target.value)}
                    placeholder="Cerca accessorio…"
                    data-testid="related-search"
                    className="max-w-[220px]"
                  />
                </div>
                <div className="max-h-56 overflow-y-auto border border-xd-border rounded-md divide-y divide-xd-border">
                  {accessoriesList.length === 0 ? (
                    <div className="text-xs text-xd-muted px-3 py-4 text-center">Nessun accessorio disponibile ancora. Marca un prodotto come &quot;accessorio&quot; per farlo apparire qui.</div>
                  ) : (
                    accessoriesList
                      .filter((a) => a.id !== editing?.id)
                      .filter((a) => {
                        const q2 = relatedQuery.trim().toLowerCase();
                        if (!q2) return true;
                        return (a.name || "").toLowerCase().includes(q2) || (a.brand || "").toLowerCase().includes(q2);
                      })
                      .slice(0, 200)
                      .map((a) => {
                        const checked = (form.related_product_ids || []).includes(a.id);
                        return (
                          <label key={a.id} data-testid={`related-option-${a.id}`} className={`flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-slate-50 ${checked ? "bg-emerald-50/50" : ""}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const ids = new Set(form.related_product_ids || []);
                                if (e.target.checked) ids.add(a.id); else ids.delete(a.id);
                                setForm({ ...form, related_product_ids: Array.from(ids) });
                              }}
                              className="w-4 h-4 accent-emerald-600"
                            />
                            <span className="flex-1 min-w-0 truncate">{a.name}</span>
                            <span className="text-[10px] text-xd-muted whitespace-nowrap font-mono-tech">{a.brand} · € {Number(a.price || 0).toFixed(2)}</span>
                          </label>
                        );
                      })
                  )}
                </div>
              </div>
            )}
            <textarea placeholder="Specifiche (una per riga: chiave: valore)" rows={4} value={specsText} onChange={(e) => setForm({ ...form, specs: parseSpecs(e.target.value) })} className="col-span-2 border border-xd-border rounded-md px-3 py-2 text-sm font-mono text-xs" />

            {/* Inline photo uploader — carica direttamente dal telefono/PC senza aprire il dialog separato */}
            <div className="col-span-2 rounded-lg border border-xd-border bg-white p-3 space-y-3" data-testid="inline-photo-uploader">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-medium">Foto prodotto</div>
                  <div className="text-[11px] text-xd-muted">{(form.images || []).length} foto · la prima è quella principale mostrata in catalogo</div>
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingInline}
                  data-testid="inline-photo-upload-btn"
                  className="btn-cta rounded-lg px-4 py-2 text-xs font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                >
                  {uploadingInline ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploadingInline ? "Caricamento…" : "Carica dal dispositivo"}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleInlineUpload(e.target.files)}
                  data-testid="inline-photo-file-input"
                  className="hidden"
                />
              </div>
              <div
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); handleInlineUpload(e.dataTransfer.files); }}
                data-testid="inline-photo-dropzone"
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center justify-center gap-1 py-6 border-2 border-dashed border-xd-border rounded-lg cursor-pointer hover:border-xd-cyan hover:bg-xd-cyan/5 transition-colors text-center"
              >
                <Upload className="w-5 h-5 text-xd-cyanHover" />
                <span className="text-sm text-xd-text font-medium">Clicca o trascina qui le foto</span>
                <span className="text-[11px] text-xd-muted">Dallo smartphone puoi scegliere dalla galleria o scattare al momento · JPG/PNG/WEBP · max 8MB</span>
              </div>
              {(form.images || []).length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                  {(form.images || []).map((url, i) => (
                    <div key={url + i} data-testid={`inline-photo-${i}`} className="relative group aspect-square bg-slate-100 rounded-md overflow-hidden border border-xd-border">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      {i === 0 && (
                        <span className="absolute top-1 left-1 bg-xd-cyan text-white text-[9px] font-mono-tech uppercase tracking-widest px-1.5 py-0.5 rounded">Principale</span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeInlineImage(url)}
                        data-testid={`inline-photo-delete-${i}`}
                        title="Rimuovi"
                        className="absolute top-1 right-1 w-6 h-6 rounded-md bg-white/90 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-500 hover:text-white"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <details className="text-[11px] text-xd-muted">
                <summary className="cursor-pointer hover:text-xd-text">Oppure incolla URL immagini (avanzato)</summary>
                <textarea placeholder="Un URL per riga" rows={3} value={Array.isArray(form.images) ? form.images.join("\n") : form.images} onChange={(e) => setForm({ ...form, images: e.target.value.split("\n").filter(Boolean) })} className="mt-2 w-full border border-xd-border rounded-md px-3 py-2 text-sm font-mono text-xs" />
              </details>
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 text-sm">Annulla</button>
            <button type="button" onClick={save} data-testid="product-save" className="btn-cta rounded-lg px-4 py-2 text-sm">Salva</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
